import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getEvents } from "../lib/api";
import type { RaceDayEvent } from "../lib/types";
import { useWatchList } from "../lib/useWatchList";

export default function Events() {
  const [events, setEvents] = useState<RaceDayEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const watch = useWatchList();

  useEffect(() => {
    let cancelled = false;
    getEvents()
      .then((res) => {
        if (!cancelled) setEvents(res.events);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!events) return null;
    const q = filter.trim().toLowerCase();
    if (!q) return events;
    return events.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        (e.city ?? "").toLowerCase().includes(q) ||
        (e.state ?? "").toLowerCase().includes(q),
    );
  }, [events, filter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <h1 className="text-xl font-mono font-bold">Events</h1>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filter by name or location"
          className="px-3 py-1.5 rounded-md border border-pit-border bg-pit-bg text-sm font-mono focus:outline-none focus:border-pit-accent w-full sm:w-72"
        />
      </div>

      {error && <div className="text-sm text-pit-accent font-mono">{error}</div>}
      {!events && !error && <div className="text-sm text-pit-muted font-mono">Loading events…</div>}

      <div className="rounded-md border border-pit-border pit-panel divide-y divide-pit-border">
        {filtered?.map((event) => {
          const watched = watch.events.includes(event.id);
          return (
            <Link
              key={event.id}
              to={`/event/${event.id}`}
              className="flex items-center gap-4 p-3 hover:bg-pit-border/30 transition-colors"
            >
              <div className="w-12 h-12 flex-shrink-0 rounded bg-pit-bg flex items-center justify-center overflow-hidden">
                {event.imgUrl ? (
                  <img
                    src={event.imgUrl}
                    alt=""
                    className="max-w-full max-h-full"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm font-bold flex items-center gap-2 truncate">
                  {event.name}
                  {watched && <span className="pill bg-pit-gold/20 text-pit-gold">watched</span>}
                </div>
                <div className="text-xs text-pit-muted font-mono mt-0.5 flex flex-wrap gap-2">
                  <span>{event.date}</span>
                  {event.type && <span>· {event.type}</span>}
                  {(event.city || event.state) && (
                    <span>· {[event.city, event.state].filter(Boolean).join(", ")}</span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
        {filtered && filtered.length === 0 && (
          <div className="p-6 text-sm text-pit-muted font-mono text-center">
            No events match.
          </div>
        )}
      </div>
    </div>
  );
}
